import {hpcAttrs} from '@github-ui/hpc'
import {
  IpmErrorBoundary,
  reportIpmAction,
  reportIpmDismiss,
  reportIpmVisible,
  type IpmMetricTags,
  useIpmRenderTiming,
} from '@github-ui/ipm-telemetry'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {XIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {Profiler, useEffect, useMemo, useRef, useState} from 'react'
import styles from './SidebarNudge.module.css'

export interface SidebarNudgeAnalytics {
  category: string
  location: string
  ctaAction: string
  organizationId?: number | string
  plan?: string
}

export interface SidebarNudgeProps {
  title: string
  dateLabel: string
  imagePathLight: string
  imagePathDark: string
  imageAltLight?: string
  imageAltDark?: string
  imageAspectRatio?: string
  description?: string
  topics?: string[]
  ctaTitle: string
  ctaHref: string
  dismissPath: string
  analytics: SidebarNudgeAnalytics
  testSelector: string
}

interface SidebarNudgeBaseProps extends SidebarNudgeProps {
  telemetryComponent: string
  dataTestId: string
}

export function SidebarNudgeBase({
  title,
  dateLabel,
  imagePathLight,
  imagePathDark,
  imageAltLight = '',
  imageAltDark = '',
  imageAspectRatio,
  description,
  topics,
  ctaTitle,
  ctaHref,
  dismissPath,
  analytics,
  testSelector,
  telemetryComponent,
  dataTestId,
}: SidebarNudgeBaseProps) {
  const [hidden, setHidden] = useState(false)
  const dismissAnnouncementRef = useRef<HTMLSpanElement>(null)
  const telemetryTags = useMemo<IpmMetricTags>(
    () => ({
      package: 'in-product-targeting',
      component: telemetryComponent,
      surface: 'sidebar_nudge',
      display_location: analytics.location,
      category: analytics.category,
      nudge_id: testSelector,
      variant: 'default',
    }),
    [analytics.category, analytics.location, telemetryComponent, testSelector],
  )
  const renderTiming = useIpmRenderTiming(telemetryTags)

  useEffect(() => {
    reportIpmVisible(telemetryTags)
  }, [telemetryTags])

  const handleDismiss = async () => {
    reportIpmDismiss(telemetryTags)
    try {
      await verifiedFetch(dismissPath, {method: 'POST'})
    } catch {
      reportIpmAction('error', telemetryTags, {samplingProbability: 1})
      // Silently handle network errors to maintain good UX
    } finally {
      setHidden(true)
    }
  }

  // After the card unmounts, move focus to the polite live region that
  // announces the dismissal. Without this, keyboard / screen-reader users get
  // dumped onto <body> when the focused dismiss button is removed (WCAG 2.4.3).
  useEffect(() => {
    if (hidden) {
      dismissAnnouncementRef.current?.focus()
    }
  }, [hidden])

  if (hidden) {
    return (
      <span
        ref={dismissAnnouncementRef}
        tabIndex={-1}
        role="status"
        aria-live="polite"
        className={styles.dismissAnnouncement}
      >
        Nudge dismissed.
      </span>
    )
  }

  const analyticsLabel = `location:${analytics.location}`
  const imageStyle = imageAspectRatio ? {aspectRatio: imageAspectRatio} : undefined

  const nudge = (
    <>
      <span data-test-selector={testSelector} />
      <div
        className={styles.container}
        {...hpcAttrs()}
        data-testid={dataTestId}
        data-analytics-visible={JSON.stringify({
          category: analytics.category,
          action: 'visible',
          label: analyticsLabel,
          ...(analytics.organizationId !== undefined && {organizationId: analytics.organizationId}),
          ...(analytics.plan !== undefined && {plan: analytics.plan}),
        })}
      >
        <div className={styles.dismiss}>
          <IconButton
            icon={XIcon}
            variant="invisible"
            aria-label="Dismiss"
            onClick={handleDismiss}
            data-analytics-event={JSON.stringify({
              category: analytics.category,
              action: 'dismiss',
              label: analyticsLabel,
            })}
          />
        </div>

        <img
          src={imagePathLight}
          alt={imageAltLight}
          className={`${styles.image} ${styles.imageLight}`}
          style={imageStyle}
        />
        <img
          src={imagePathDark}
          alt={imageAltDark}
          className={`${styles.image} ${styles.imageDark}`}
          style={imageStyle}
        />

        <div className={styles.date}>{dateLabel}</div>

        <div className={styles.main}>
          <h3 className={styles.title}>{title}</h3>

          {description ? <p className={styles.description}>{description}</p> : null}

          {topics && topics.length > 0 ? (
            // Safari + VoiceOver drop list semantics from a <ul> whose
            // list-style is `none`. Restate the role explicitly to keep
            // assistive tech announcing the topics as a list.
            // eslint-disable-next-line jsx-a11y/no-redundant-roles
            <ul className={styles.topics} role="list">
              {topics.map(topic => (
                <li key={topic} className={styles.topic}>
                  {topic}
                </li>
              ))}
            </ul>
          ) : null}

          {/* `btn` and `btn-sm` provide layout + size; colors come from the CSS
              module so we don't fight `btn-primary` specificity with !important. */}
          <a
            href={ctaHref}
            onClick={() => reportIpmAction('cta_click', telemetryTags)}
            className={`${styles.cta} btn btn-sm text-small px-2`}
            data-test-selector={`${testSelector}-cta`}
            data-analytics-event={JSON.stringify({
              category: analytics.category,
              action: analytics.ctaAction,
              label: analyticsLabel,
            })}
          >
            {ctaTitle}
          </a>
        </div>
      </div>
    </>
  )

  const content = !renderTiming.isEnabled ? (
    nudge
  ) : (
    <Profiler id={telemetryComponent} onRender={renderTiming.onRender}>
      {nudge}
    </Profiler>
  )

  return (
    <IpmErrorBoundary tags={telemetryTags} boundaryName={telemetryComponent}>
      {content}
    </IpmErrorBoundary>
  )
}

export function SidebarNudge(props: SidebarNudgeProps) {
  return <SidebarNudgeBase {...props} telemetryComponent="SidebarNudge" dataTestId="sidebar-nudge" />
}
